// Global Instructions Rule Applied!
import { useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import {
  PipelineStageEntry,
  validatePipelineStageEntry
} from '../../../state_manager/data_models/pipeline_stage_entry.ts'
import { PIPELINE_TYPE_FUNDRAISING, VIEW_TYPE_KANBAN_BOARD } from '../../../state_manager/globals.ts'
import { PipelineStagesModelFields } from '../../../state_manager/data_models/pipeline_stages_model.ts'
import { FIELD_KEY_PROG } from '../../../state_manager/globals.ts'
import type { RootState } from '../../../state_manager/store.ts'

export function useFundraisingManager() {
  const pipelines = useSelector((state: RootState) => state.globalConfigs?.pipelines || [])
  const pipelineStages = useSelector(
    (state: { globalConfigs?: { pipeline_stages?: unknown[] } }) => state.globalConfigs?.pipeline_stages || []
  )
  const pipelineConfigs = useSelector(
    (state: { globalConfigs?: { pipeline_configs?: unknown[] } }) => state.globalConfigs?.pipeline_configs || []
  )

  const defaultPipelineId = useMemo((): string | null => {
    const defaultPipeline = pipelines.find(
      (pipeline) => pipeline.default === true && pipeline.type === PIPELINE_TYPE_FUNDRAISING
    )

    return defaultPipeline?.id || null
  }, [pipelines])

  const defaultFundId = useMemo((): string | null => {
    const defaultPipeline = pipelines.find(
      (pipeline) => pipeline.default === true && pipeline.type === PIPELINE_TYPE_FUNDRAISING
    )

    return defaultPipeline?.default_fund || null
  }, [pipelines])

  const getPipelineStages = useCallback(
    (pipelineId: string, viewType: typeof VIEW_TYPE_KANBAN_BOARD | 'tbl'): PipelineStageEntry[] => {
      if (!pipelineId) return []

      const filteredConfigs = (pipelineConfigs as Array<{ pipeline?: string; [key: string]: unknown }>).filter(
        (config) => config.pipeline === pipelineId && config[`visible_${viewType}`] === true
      )

      const filteredStages = (
        pipelineStages as Array<{ id?: string; name?: string | null; decision_status?: string | null }>
      ).filter((stage) => filteredConfigs.some((config) => config.stage === stage.id))

      const result: PipelineStageEntry[] = []

      for (const config of filteredConfigs) {
        const stage = filteredStages.find(
          (stage: { id?: string; name?: string | null; decision_status?: string | null }) => stage.id === config.stage
        )
        if (stage) {
          result.push({
            ...config,
            [`${FIELD_KEY_PROG}${PipelineStagesModelFields.name}`]: stage.name,
            [`${FIELD_KEY_PROG}${PipelineStagesModelFields.decision_status}`]: stage.decision_status
          } as PipelineStageEntry)
        }
      }

      const seqKey = viewType === VIEW_TYPE_KANBAN_BOARD ? 'sequence_kb' : 'sequence_tbl'
      const sortedStages = [...result].sort((a, b) => {
        const seqA = (a[seqKey] as number | null | undefined) ?? Number.MAX_SAFE_INTEGER
        const seqB = (b[seqKey] as number | null | undefined) ?? Number.MAX_SAFE_INTEGER
        return seqA - seqB
      })

      const validatedData = validatePipelineStageEntry(sortedStages, { isArray: true }) as PipelineStageEntry[]

      return validatedData
    },
    [pipelineConfigs, pipelineStages]
  )

  return {
    defaultPipelineId,
    defaultFundId,
    getPipelineStages
  }
}
